import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetAgreementImportsBySessionIdContractsQuery,
  usePostAgreementImportsBySessionIdContractsAndContractIdApproveMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelperText,
  InfoTooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Typography,
} from '@client/toolkit';

import { ContractStatusBadge } from './contract-status-badge';
import { extractErrorCodes } from '../utils/extract-error-codes';

interface StepApproveProps {
  sessionId: string;
  onReviewContract: (contractId: string) => void;
  onFinish: () => void;
  onBack: () => void;
}

/**
 * Step 5 — approve & transfer (Phase 5). Approving a contract creates a Draft agreement;
 * it never auto-transitions to Effective (decision 4). Contracts that still have
 * <c>NeedsReview</c> lines can't be approved and are routed back to Step 4 instead.
 */
export function StepApprove({ sessionId, onReviewContract, onFinish, onBack }: StepApproveProps) {
  const { t: tAgreements } = useTranslation('agreements');
  const { t: tCommon } = useTranslation('common');

  const { data: contracts, isLoading } = useGetAgreementImportsBySessionIdContractsQuery({ sessionId });
  const items = useMemo(() => contracts ?? [], [contracts]);

  const [approveContract, { isLoading: isApproving }] =
    usePostAgreementImportsBySessionIdContractsAndContractIdApproveMutation();
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string[]>>({});

  const handleApprove = async (contractId: string) => {
    setApprovingId(contractId);
    setRowErrors((prev) => ({ ...prev, [contractId]: [] }));
    try {
      await approveContract({ sessionId, contractId }).unwrap();
    } catch (err) {
      setRowErrors((prev) => ({ ...prev, [contractId]: extractErrorCodes(err) }));
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">{tAgreements('agreementImport.approve.title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <Typography variant="secondary">{tAgreements('agreementImport.approve.description')}</Typography>

        <Table className="overflow-hidden rounded-md">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{tAgreements('agreementImport.contracts.documentId')}</TableHeadCell>
              <TableHeadCell>{tAgreements('agreementImport.contracts.tenant')}</TableHeadCell>
              <TableHeadCell>{tAgreements('agreementImport.contracts.status')}</TableHeadCell>
              <TableHeadCell />
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {!isLoading &&
              items.map((contract) => {
                const needsReview = contract.status === 'NeedsReview';
                const isApproved = contract.status === 'Approved';
                const errors = rowErrors[contract.id] ?? [];

                return (
                  <TableRow key={contract.id} verticalAlign="middle">
                    <TableCell>{contract.documentId}</TableCell>
                    <TableCell>{contract.rawSourceData.tenantName ?? '—'}</TableCell>
                    <TableCell>
                      <ContractStatusBadge status={contract.status} />
                      {errors.length > 0 && (
                        <HelperText error>
                          {errors
                            .map((code) =>
                              tAgreements(`agreementImport.validationErrors.${code}`, { defaultValue: code }),
                            )
                            .join(', ')}
                        </HelperText>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {needsReview ? (
                        <InfoTooltip message={tAgreements('agreementImport.approve.needsReviewTooltip')}>
                          <Button size="xs" color="primary-invert" onClick={() => onReviewContract(contract.id)}>
                            {tAgreements('agreementImport.contracts.reviewButton')}
                          </Button>
                        </InfoTooltip>
                      ) : (
                        <Button
                          size="xs"
                          color="primary"
                          disabled={isApproved}
                          progressing={isApproving && approvingId === contract.id}
                          onClick={() => handleApprove(contract.id)}
                        >
                          {tAgreements('agreementImport.approve.approveButton')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>

        <div className="flex justify-between">
          <Button color="primary-dark" onClick={onBack}>
            {tCommon('back')}
          </Button>
          <Button color="primary" onClick={onFinish}>
            {tAgreements('agreementImport.approve.finishButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
